import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(monto, moneda = 'ARS') {
  if (monto === null || monto === undefined) return '-';

  const cur = (typeof moneda === 'string' && /^[A-Za-z]{3}$/.test(moneda)) ? moneda.toUpperCase() : 'ARS';
  try {
    const formatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(monto);
  } catch {
    return `$ ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(monto)}`;
  }
}

// Format number with thousands separator
export function formatNumber(num) {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('es-AR').format(num);
}

// Format percentage
export function formatPercentage(value) {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(2)}%`;
}

// Convert currency based on cotizacion
export function convertCurrency(monto, monedaOrigen, monedaDestino, cotizacion) {
  if (!cotizacion || monedaOrigen === monedaDestino) {
    return { monto, moneda: monedaOrigen, converted: false };
  }
  
  let montoConvertido;
  if (monedaOrigen === 'ARS' && monedaDestino === 'USD') {
    montoConvertido = monto / cotizacion;
  } else if (monedaOrigen === 'USD' && monedaDestino === 'ARS') {
    montoConvertido = monto * cotizacion;
  } else {
    return { monto, moneda: monedaOrigen, converted: false };
  }
  
  return { monto: montoConvertido, moneda: monedaDestino, converted: true };
}

// Format period (YYYY-MM) to readable format
export function formatPeriodo(periodo) {
  if (!periodo) return '-';
  const [year, month] = periodo.split('-');
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  return `${months[parseInt(month) - 1]} ${year}`;
}

// Get current period
export function getCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

// Get previous periods
export function getPreviousPeriods(count = 12) {
  const periods = [];
  const now = new Date();
  
  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    periods.push(`${year}-${month}`);
  }
  
  return periods;
}

// Períodos para el módulo de Facturación: incluye el MES SIGUIENTE solo durante los
// últimos 5 días del mes en curso (para emitir facturas del mes próximo por adelantado).
export function getFacturacionPeriods(count = 24) {
  const periods = getPreviousPeriods(count);
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  if (now.getDate() >= daysInMonth - 4) {
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextKey = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
    return [nextKey, ...periods];
  }
  return periods;
}


// Format date
export function formatDate(dateString) {
  if (!dateString) return '-';
  // Fechas "YYYY-MM-DD" (sin hora) se interpretan como fecha LOCAL, no UTC:
  // new Date("2026-10-05") es medianoche UTC y en UTC-3 muestra el día anterior.
  const iso = String(dateString);
  const date = iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// Format date from YYYY-MM-DD to DD/MM/YYYY
export function formatDateDMY(dateString) {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateString;
}

// Parse date from DD/MM/YYYY to YYYY-MM-DD
export function parseDateDMY(dateString) {
  if (!dateString) return '';
  const parts = dateString.split('/');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
  }
  return dateString;
}

// Format currency with comma as decimal separator (es-AR format)
export function formatImporte(monto, decimals = 2) {
  if (monto === null || monto === undefined) return '-';
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(monto);
}

// Check if user has role (considera rol primario Y roles_extra)
export function hasRole(user, roles) {
  if (!user) return false;
  const userRoles = [user.rol, ...(user.roles_extra || [])].filter(Boolean);
  if (Array.isArray(roles)) {
    return roles.some((r) => userRoles.includes(r));
  }
  return userRoles.includes(roles);
}

// Get status badge classes
export function getStatusBadgeClass(status) {
  switch (status) {
    case 'alquilada':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'parcial':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'vacante':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'borrador':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'publicado':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'activo':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'inactivo':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

// Get status label
export function getStatusLabel(status) {
  const labels = {
    alquilada: 'Alquilada',
    parcial: 'Parcial',
    vacante: 'Vacante',
    borrador: 'Borrador',
    publicado: 'Publicado',
    activo: 'Activo',
    inactivo: 'Inactivo',
    persona_humana: 'Persona Humana',
    persona_fisica: 'Persona Humana', // Legacy support
    persona_juridica: 'Persona Jurídica',
    oficina: 'Oficina',
    local: 'Local',
    cowork: 'Cowork',
    cochera: 'Cochera'
  };
  return labels[status] || status;
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
